// src/components/Navbar/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import './Navbar.css';
import Dropdown from '../../components/Navbar/Dropdown';
import myLogo from '../../assets/new_logo.webp';
import { HiMenu, HiX } from 'react-icons/hi';
import lightmode from '../../assets/light-mode.webp';
import darkmode from '../../assets/dark-mode.webp';
import MiniCart from '../../components/MiniCart/MiniCart';
import { useModal } from '../../context/ModalContext';
import { FaUserCircle } from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode';
import { FiLogIn } from 'react-icons/fi'; // Lisätty kirjautumis- ja uloskirjautumisikonit
import { FiChevronDown } from 'react-icons/fi';

const Navbar = ({ theme, setTheme }) => {
  const { isModalOpen } = useModal();
  const location = useLocation();
  const navigate = useNavigate();

  const [click, setClick] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 881);
  const [cartCount, setCartCount] = useState(0);
  const [showNavbar, setShowNavbar] = useState(true);

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const [userName, setUserName] = useState('');
  const [profileType, setProfileType] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Hae käyttäjän tiedot
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);

    const profile = localStorage.getItem('profileType');
    if (token) {
      try {
        const { email } = jwtDecode(token);
        setUserName(email.split('@')[0]);
      } catch (error) {
        console.error('Virhe dekoodatessa tokenia:', error);
      }
    }
    if (profile) {
      setProfileType(profile);
    }
  }, []);

  // Navbar näkyvyys
  useEffect(() => {
    const modalOpen = sessionStorage.getItem('isModalOpen') === 'true';
    const isBandPage = location.pathname === '/esiintyjat';
    setShowNavbar(!(isBandPage && modalOpen));
  }, [isModalOpen, location.pathname]);

  // Korin määrä & ikkunaresize
  useEffect(() => {
    const updateCartCount = () => {
      try {
        const items = JSON.parse(localStorage.getItem('rentalCart')) || [];
        setCartCount(items.reduce((sum, i) => sum + (i.quantity || 0), 0));
      } catch {
        setCartCount(0);
      }
    };
    const handleResize = () => {
      const mobile = window.innerWidth <= 881;
      setIsMobile(mobile);
      if (mobile) setDropdown(false);
      else {
        setClick(false);
        setDropdown(false);
      }
    };

    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('focus', updateCartCount);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('focus', updateCartCount);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Sulje käyttäjävalikko klikkaamalla ulos
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = () => {
    setClick(!click);
    if (!click) setDropdown(false);
  };
  const closeMobileMenu = () => {
    setClick(false);
    setDropdown(false);
  };
  const handleMouseEnter = () => !isMobile && setDropdown(true);
  const handleMouseLeave = () => !isMobile && setDropdown(false);

  const handleLogoutClick = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('profileType');
    window.location.href = '/kirjaudu';
  };
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleDashboardRedirect = () => {
    setUserDropdownOpen(false);
    scrollToTop();
    setTimeout(() => {
      if (profileType === 'rental') navigate('/rental-dashboard');
      else if (profileType === 'performer') navigate('/performer-dashboard');
      else navigate('/kirjaudu');
    }, 300);
  };

  if (!showNavbar) return null;

  return (
    <div className="navbar-wrapper">
      <nav className="fixed top-0 left-0 w-full z-[9999] navbar dark:bg-violet bg-primary shadow-md">
        {/* Vasen puoli */}
        <div className="dark:text-primary navbar-logo font-bold font-customFont2 text-darkest flex items-center justify-center md:justify-start text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 3xl:text-3xl">
          <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
            <img src={myLogo} alt="Logo" style={{ width: '40px' }} />
            <span className="navbar-companynametext ml-2 xxxsm:hidden xxsm:hidden sm:text-sm md:text-base lg:text-base xl:text-base 2xl:text-base dark:text-shadow-xxl">
            Spiderlegs Productions
          </span>

          </Link>
          <button
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            aria-label="Vaihda teemaa"
            className="toggle-button focus:outline-none ml-4"
          >
            <img
              src={theme === 'light' ? darkmode : lightmode}
              alt="mode"
              className="toggle-icon rotating"
            />
          </button>
        </div>

        {/* Keskiosa: menu */}
        <div className="absolute right-20 top-0 transform -translate-x-1/2 z-[9999]">
        <button
          aria-label="Menu"
          className={`menu-icon dark:text-primary text-darkest ${click ? 'active' : ''}`}
          onClick={handleMenuClick}
        >
          {click ? <HiX size={20} /> : <HiMenu size={40} />}
        </button>
      </div>

        {/* Linkit */}
        <ul
          className={`${theme === 'dark' ? 'dark:bg-violet' : 'bg-primary'} ${click ? 'nav-menu active' : 'nav-menu'}`}
          style={{ zIndex: 99999 }} // tämä lisää z-index suoraan naville
        >
          <li className="nav-item"><Link to="/" className="nav-links" onClick={closeMobileMenu}>Koti</Link></li>
          <li className="nav-item relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <Link to="/vuokrattavat" className="nav-links" onClick={closeMobileMenu}>Vuokrattavat</Link>
            {dropdown && <Dropdown onLinkClick={closeMobileMenu} />}
          </li>
          <li className="nav-item"><Link to="/esiintyjat" className="nav-links" onClick={closeMobileMenu} state={{ skipModal: true }}>Bändit ja esiintyjät</Link></li>
          <li className="nav-item"><Link to="/meista" className="nav-links" onClick={closeMobileMenu}>Meistä</Link></li>
          <li className="nav-item"><Link to="/UKK" className="nav-links" onClick={closeMobileMenu}>UKK</Link></li>
          <li className="nav-item"><Link to="/tee-varaus" className="nav-links" onClick={closeMobileMenu}>Tee varaus</Link></li>
        </ul>

  {/* Oikea puoli: käyttäjä/kori */}
  <div className="relative flex items-center gap-4">
    {isLoggedIn ? (
      // Kirjautunut
      <div ref={userDropdownRef} className="relative">
        <button
          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
          className="flex items-center gap-2 focus:outline-none dark:text-primary text-darkest"
          aria-label="Käyttäjävalikko"
        >
          {/* Ikoni näkyy xxxsm–xsm */}
          <FaUserCircle
            size={28}
            className="
              xxxsm:inline xxsm:inline s24:inline xsm:inline
              sm:inline md:inline mdd:inline lg:inline xl:hidden 2xl:hidden mb-[3px]
            "
          />
          {/* Teksti näkyy sm–2xl */}
          <span
            className="
              xxxsm:hidden xxsm:hidden s24:hidden xsm:hidden
              sm:hidden md:hidden mdd:hidden lg:hidden xl:inline 2xl:inline
              text-sm font-customFont1 flex items-center
            "
          >
            {userName}
            <FiChevronDown className="ml-1 inline-block animate-pulse" />
          </span>
        </button>

        {userDropdownOpen && (
          <div className="absolute right-0 mt-2 w-44 bg-primary dark:bg-violet shadow-2xl p-4 rounded-bl-lg z-[99999]">
            <button
              onClick={handleDashboardRedirect}
              className="w-full text-left px-4 py-2 font-customFont1 text-darkest dark:text-primary text-sm hover:bg-gray-100 dark:hover:bg-violet rounded-t-lg"
            >
              Oma tili
            </button>
            <button
              onClick={handleLogoutClick}
              className="w-full text-left px-4 py-2 font-customFont1 text-darkest dark:text-primary dark:text-shadow-lg text-sm hover:bg-gray-100 dark:hover:bg-violet rounded-b-lg flex items-center gap-2"
            >
              <span>Kirjaudu ulos</span>
            </button>
          </div>
        )}
      </div>
    ) : (
      // Ei kirjautunut
      <Link
        to="/kirjaudu"
        className="flex text-center items-center font-customFont1 text-darkest hover:underline"
      >
        {/* Ikoni näkyy xxxsm–xsm */}
        <FiLogIn
          className="
            text-3xl dark:text-shadow-lg
            xxxsm:inline xxsm:inline s24:inline xsm:inline
            sm:inline md:inline mdd:inline lg:inline xl:hidden 2xl:hidden mb-[4px] 
          "
          aria-label="Kirjaudu sisään"
        />
        {/* Teksti näkyy sm–2xl */}
        <span
          className="
            xxxsm:hidden xxsm:hidden s24:hidden xsm:hidden
            sm:hidden md:hidden mdd:hidden lg:hidden xl:inline 2xl:inline dark:text-shadow-lg
          "
        >
          Kirjaudu sisään
        </span>
      </Link>
    )}

    <div className="relative 
      xxxsm:mr-8 
      xxsm:mr-8 
      s24:mr-8 
      xsm:mr-8 
      sm:mr-8 
      md:mr-8 
      mdd:mr-8 
      lg:mr-8 
      xl:mr-8 
      2xl:mr-8 
      mt-[2px] z-0"> 
      
      {/*screens: {
        'xxxsm': { 'min': '320px', 'max': '374px' },
        'xxsm': { 'min': '375px', 'max': '389px' },
        's24': { 'min': '390px', 'max': '429px' },
        'xsm': { 'min': '430px', 'max': '639px' },
        'sm': { 'min': '640px', 'max': '767px' },
        'md': { 'min': '768px', 'max': '880px' },
        'mdd': { 'min': '881px', 'max': '1023px' },
        'lg': { 'min': '1024px', 'max': '1279px' },
        'xl': { 'min': '1280px', 'max': '1535px' },
        '2xl': { 'min': '1536px' }, */}
      <MiniCart cartCount={cartCount} />
    </div>
  </div>

      </nav>

      {/* overlay mobiilissa */}
      <div
        className={click ? 'overlay active' : 'overlay'}
        onClick={closeMobileMenu}
        onKeyDown={(e) => e.key === 'Enter' && closeMobileMenu()}
        role="button"
        tabIndex={0}
      />
    </div>
  );
};

Navbar.propTypes = {
  theme: PropTypes.string.isRequired,
  setTheme: PropTypes.func.isRequired,
};

export default Navbar;
